import { foldPosition, type SessionTiming } from '../av/sessionClock';

/**
 * Video export frame policy and settings (Phase 4B). Pure.
 *
 * Frame i of an export is rendered at the unwrapped export time
 *
 *     τᵢ = rangeStart + i / fps
 *
 * computed per frame (never accumulated), and shows the session position
 * `foldPosition(τᵢ, loop)`, exactly where the live transport would be after playing
 * τᵢ − rangeStart seconds from rangeStart. Frame count: `ceil(duration · fps)` (a
 * fractional last frame becomes a whole frame), so every frame time is strictly before
 * the range end and the video is at most one frame period longer than requested.
 */

export const VIDEO_FPS = [12, 24, 30, 60] as const;
export type VideoFps = (typeof VIDEO_FPS)[number];

export const VIDEO_RESOLUTIONS = {
  '480p': { width: 854, height: 480 },
  '720p': { width: 1280, height: 720 },
  '1080p': { width: 1920, height: 1080 },
} as const;
export type VideoResolution = keyof typeof VIDEO_RESOLUTIONS;

export type VideoQuality = 'standard' | 'high';
export type VideoCodec = 'vp8' | 'vp9';
export type ExportMethod = 'offline' | 'realtime';

/** Longest export: the WAV export limit (offline session audio is rendered in memory: ~23 MB per minute). */
export const MAX_VIDEO_SECONDS = 300;
/** Refuse exports whose estimated file would exceed this (memory on ~4 GB machines). */
export const MAX_VIDEO_BYTES = 400 * 1024 * 1024;
/**
 * Hard limit on the encoded bytes actually produced (the estimate is bitrate × duration,
 * and variable-bitrate encoders can overshoot it). Checked as output arrives, on both the
 * offline and the real-time path; reaching it fails the export.
 */
export const MAX_OUTPUT_BYTES = 480 * 1024 * 1024;
/**
 * Real-time recordings up to this size are re-muxed (duration and cues added). Re-muxing
 * holds the recording, a copy of its bytes and the new file at once (about 3× its size);
 * larger recordings are kept exactly as recorded, which is still valid WebM.
 */
export const MAX_REMUX_BYTES = 128 * 1024 * 1024;

/** Early stop: an export whose projected size passes the hard limit by this factor stops before reaching it. */
export const PROJECTION_MARGIN = 1.1;

/**
 * Output-size check. `fixed` bytes are already final (encoded audio); `bytes` grow with
 * the work done (`done` of `total` units: frames or seconds). True when the output has
 * passed `limit`, or — once at least `minDone` units are done — when extrapolating the
 * growth to the whole export passes `limit × PROJECTION_MARGIN`, so an export that will
 * clearly overshoot stops early instead of encoding up to the limit first.
 */
export function outputTooLarge(fixed: number, bytes: number, done: number, total: number, limit: number, minDone: number): boolean {
  if (fixed + bytes > limit) return true;
  if (done < minDone || done <= 0 || total <= 0) return false;
  return fixed + (bytes / done) * total > limit * PROJECTION_MARGIN;
}

/** "480 MB" style size label. */
export const formatMb = (bytes: number): string => `${Math.round(bytes / 1024 / 1024)} MB`;
/** Opus at 48 kHz stereo. */
export const AUDIO_SAMPLE_RATE = 48000;
export const AUDIO_BITRATE = 128_000;
/** A key frame (and a new WebM cluster) every this many seconds. */
export const KEYFRAME_INTERVAL_SEC = 2;

const EPS = 1e-9;

export interface VideoSettings {
  resolution: VideoResolution;
  fps: VideoFps;
  codec: VideoCodec;
  quality: VideoQuality;
  method: ExportMethod;
  /** null: the full session. Seconds on the session timeline (unwrapped when looping). */
  range: { start: number; end: number } | null;
  includeAudio: boolean;
  fileName: string;
}

export const DEFAULT_VIDEO_SETTINGS: Omit<VideoSettings, 'fileName'> = {
  resolution: '720p',
  fps: 30,
  codec: 'vp8',
  quality: 'standard',
  method: 'offline',
  range: null,
  includeAudio: true,
};

/** Number of frames for a duration: ceil(duration · fps), at least 1. */
export function frameCount(durationSec: number, fps: number): number {
  if (!(durationSec > 0) || !(fps > 0)) return 0;
  return Math.max(1, Math.ceil(durationSec * fps - EPS));
}

/** Unwrapped export time of frame i (seconds on the session timeline). */
export const frameTime = (rangeStart: number, index: number, fps: number): number => rangeStart + index / fps;

/** Session position shown by frame i (folded into the session's loop, like the transport). */
export const framePosition = (rangeStart: number, index: number, fps: number, loop: SessionTiming['loop']): number => foldPosition(frameTime(rangeStart, index, fps), loop);

/** Container timestamp of frame i in microseconds. */
export const frameTimestampUs = (index: number, fps: number): number => Math.round((index * 1_000_000) / fps);

/** The first frame at or after an instant `seconds` after the range start. */
export const firstFrameAtOrAfter = (seconds: number, fps: number): number => Math.max(0, Math.ceil(seconds * fps - EPS));

/** True for frames that start a cluster: one key frame every KEYFRAME_INTERVAL_SEC. */
export const isKeyFrame = (index: number, fps: number): boolean => index % Math.max(1, Math.round(fps * KEYFRAME_INTERVAL_SEC)) === 0;

/** Video bitrate (bits/s) for a resolution, frame rate and quality. */
export function videoBitrate(resolution: VideoResolution, fps: number, quality: VideoQuality): number {
  const base = { '480p': 1_500_000, '720p': 3_000_000, '1080p': 6_000_000 }[resolution];
  const q = quality === 'high' ? 5 / 3 : 1;
  const motion = Math.min(1.5, Math.max(0.6, fps / 30));
  return Math.round(base * q * motion);
}

export interface ExportEstimate {
  durationSec: number;
  frames: number;
  /** Rough output size (bitrate × duration). */
  bytes: number;
  /** Settings that are slow or heavy on modest hardware. */
  expensive: string[];
}

export function estimateExport(settings: Pick<VideoSettings, 'resolution' | 'fps' | 'quality' | 'includeAudio'>, durationSec: number): ExportEstimate {
  const frames = frameCount(durationSec, settings.fps);
  const videoSec = frames / settings.fps;
  const bits = videoBitrate(settings.resolution, settings.fps, settings.quality) + (settings.includeAudio ? AUDIO_BITRATE : 0);
  const bytes = Math.round((bits * videoSec) / 8);
  const expensive: string[] = [];
  if (settings.resolution === '1080p') expensive.push('1080p renders and encodes 2.25× the pixels of 720p.');
  if (settings.fps === 60) expensive.push('60 fps renders twice as many frames as 30 fps.');
  if (durationSec > 180) expensive.push('Exports longer than 3 minutes take a while on modest hardware.');
  if (frames > 10_800) expensive.push(`${frames.toLocaleString('en-US')} frames to render.`);
  return { durationSec, frames, bytes, expensive };
}

export type RangeCheck = { ok: true; start: number; end: number } | { ok: false; error: string };

/** Validates an export range against the session length (null: the full session). */
export function checkRange(range: VideoSettings['range'], sessionSec: number, fps: number): RangeCheck {
  const start = range?.start ?? 0;
  const end = range?.end ?? sessionSec;
  if (!Number.isFinite(start) || !Number.isFinite(end)) return { ok: false, error: 'The range start and end must be numbers.' };
  if (!(sessionSec > 0)) return { ok: false, error: 'The session has no length to export.' };
  if (start < 0) return { ok: false, error: 'The range cannot start before 0 s.' };
  if (end > sessionSec + 1e-6) return { ok: false, error: `The range ends after the session (${sessionSec.toFixed(2)} s).` };
  if (end - start < 1 / fps - EPS) return { ok: false, error: `The range must be at least one frame long (${(1 / fps).toFixed(3)} s at ${fps} fps).` };
  if (end - start > MAX_VIDEO_SECONDS) return { ok: false, error: `Video export is limited to ${MAX_VIDEO_SECONDS / 60} minutes.` };
  return { ok: true, start, end: Math.min(end, sessionSec) };
}

export function isVideoFps(n: number): n is VideoFps {
  return (VIDEO_FPS as readonly number[]).includes(n);
}

export function isVideoResolution(r: string): r is VideoResolution {
  return Object.prototype.hasOwnProperty.call(VIDEO_RESOLUTIONS, r);
}

/** A safe download name: lower-case letters, digits, '-' and '_', at most 80 characters, plus `.webm`. */
export function sanitizeFileName(name: string, fallback = 'sonicforge-video'): string {
  const base = name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\.webm$/i, '')
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-_.]+|[-_.]+$/g, '')
    .slice(0, 80)
    .replace(/[-_]+$/g, '');
  return `${base || fallback}.webm`;
}
